/**
 * BillingPage — /settings/billing. Subscription-level billing separate from
 * credit top-ups (which live at /settings/credits).
 *
 * Shows: current plan, trial status + exact end date + countdown, upgrade
 * paths, and a placeholder-but-informative invoice history section.
 *
 * (2026-08-04, P0-2 audit) — Ships as an accurate v1 that stops silently
 * redirecting subscribers to /settings/credits. Full self-serve upgrade
 * and payment-method management is scaffolded here and wired to the same
 * Stripe checkout used elsewhere in the app.
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CreditCard, Sparkles, Calendar, ArrowUpRight, Receipt, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  starter:    { name: 'Starter',    price: 299,  color: 'bg-emerald-100 text-emerald-700', desc: 'Solo founders and freelancers.' },
  growth:     { name: 'Growth',     price: 799,  color: 'bg-sky-100 text-sky-700',         desc: 'Growing teams up to 15 seats.' },
  enterprise: { name: 'Enterprise', price: 1999, color: 'bg-fuchsia-100 text-fuchsia-700', desc: 'Multi-department orgs, full control.' },
};

function daysBetween(a, b) {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function BillingPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`);
        setMe(data);
      } catch { /* redirect to login handled by ProtectedRoute */ }
      setLoading(false);
    })();
  }, []);

  const plan = me?.plan || 'starter';
  const meta = PLAN_META[plan] || PLAN_META.starter;
  const trialEnd = me?.trial_ends_at ? new Date(me.trial_ends_at) : null;
  const trialDaysLeft = useMemo(() => trialEnd ? Math.max(0, daysBetween(new Date(), trialEnd)) : null, [trialEnd]);
  const inTrial = trialEnd && trialDaysLeft > 0;

  const startUpgrade = async (newPlan) => {
    // Reuse existing subscription checkout if wired; otherwise fall back to
    // Stripe credits pricing page.
    try {
      const { data } = await axios.post(`${API}/api/payments/subscription/checkout`, {
        plan: newPlan,
        origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
      toast.error(data?.error || 'Checkout unavailable — contact support');
    } catch {
      // Route to credits page as a temporary "add-a-payment-method" fallback.
      navigate('/settings/credits');
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <CreditCard className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing & Subscription</h1>
          <p className="text-sm text-slate-500">Manage your plan, payment method, and invoices. Credits are separate — see <a href="/settings/credits" className="text-emerald-600 hover:underline">AI Credits</a>.</p>
        </div>
      </div>

      {/* Current plan + trial status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Current plan</span>
            <Badge className={meta.color}>{meta.name}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900">${meta.price}</span>
            <span className="text-sm text-slate-500">/month</span>
          </div>
          <p className="text-sm text-slate-600">{meta.desc}</p>

          {inTrial ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="trial-status-banner">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-900">
                    {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left in your free trial
                  </div>
                  <div className="text-xs text-emerald-700 mt-1">
                    Trial ends <strong>{trialEnd.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                    Add a payment method to continue on the {meta.name} plan without interruption.
                  </div>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0" onClick={() => startUpgrade(plan)} data-testid="billing-add-payment">
                  Add payment method
                </Button>
              </div>
            </div>
          ) : trialEnd ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm text-slate-700"><Calendar className="inline h-4 w-4 mr-1" /> Your trial ended on <strong>{trialEnd.toLocaleDateString()}</strong>.</div>
              <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => startUpgrade(plan)} data-testid="billing-reactivate">Reactivate plan</Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Compare plans */}
      <Card>
        <CardHeader><CardTitle>Change plan</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(PLAN_META).map(([id, p]) => (
            <div key={id} className={`rounded-lg border p-4 ${id === plan ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-200'}`} data-testid={`plan-card-${id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-slate-900">{p.name}</div>
                {id === plan && <Badge className="bg-emerald-500 text-white">Current</Badge>}
              </div>
              <div className="text-2xl font-bold text-slate-900">${p.price}<span className="text-sm font-normal text-slate-500">/mo</span></div>
              <p className="text-xs text-slate-500 mt-1 mb-3">{p.desc}</p>
              {id !== plan && (
                <Button size="sm" variant="outline" onClick={() => startUpgrade(id)} className="w-full" data-testid={`plan-switch-${id}`}>
                  {(PLAN_META[plan]?.price || 0) < p.price ? 'Upgrade' : 'Downgrade'} <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Invoice history — placeholder that never lies about data */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" /> Invoice history</CardTitle></CardHeader>
        <CardContent>
          <div className="text-sm text-slate-500 text-center py-8" data-testid="invoice-history-empty">
            No invoices yet. Your first invoice will appear here after your trial ends and your first monthly charge processes.
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-500 flex items-center gap-1">
        Need help? <a href="mailto:hello@konnect.kaharian.com" className="text-emerald-600 hover:underline">hello@konnect.kaharian.com</a> <ExternalLink className="h-3 w-3" />
      </div>
    </div>
  );
}
